import React, { useState, useRef } from "react";
import { reorderTopics, deleteTopic, setTopicVisibility, instituteShareTopic, setTopicCategory } from "../api";
import Surface from "../theme/Surface";
import TopicCategoryManager, { LockIcon } from "./TopicCategoryManager";
import { CategoryIcon } from "../categoryIcons";

// Dark theme (DESIGN.md): every raised container uses the SAME shared surface —
// no per-topic accent colors. Selection = brighter gold border; progress = gold
// gradient. Small status badges map to the fixed semantic accents.
const BADGE = {
  private: { color: "var(--slate)", background: "transparent", borderColor: "var(--panel-line)" },
  book: { color: "var(--gold-bright)", background: "var(--gold-bg)", borderColor: "rgba(201,162,39,0.3)" },
  video: { color: "var(--tag-rose)", background: "var(--tag-rose-bg)", borderColor: "transparent" },
  grammar: { color: "var(--tag-violet)", background: "var(--tag-violet-bg)", borderColor: "transparent" },
  institute: { color: "var(--tag-teal)", background: "var(--tag-teal-bg)", borderColor: "transparent" },
};

/**
 * Sidebar topic list with optional category drill-down.
 *
 *   `topics`     — every topic the viewer may see, in global `order` (App sorts).
 *   `categories` — admin-defined groupings in sidebar order.
 *
 * Top level (selectedCategoryId == null): category cards first, then topics
 * with no category. With zero categories this renders exactly the old flat
 * list. Inside a category: a back link, the category name, and its topics.
 *
 * Edit Order reorders only the topics in the current view; on save the new
 * sequence is spliced back into the global order (same slots, new occupants),
 * so `Topic.order` stays unique and the known/new-word sequencing that reads
 * it is unaffected.
 */
export default function TopicList({
  topics,
  categories = [],
  selectedCategoryId = null,
  onSelectCategory,
  selectedTopicId,
  onSelect,
  topicProgress = {},
  isAdmin = false,
  instituteRole = "",
  onReordered,
  onCategoriesChanged,
}) {
  const [editMode, setEditMode] = useState(false);
  const [orderedTopics, setOrderedTopics] = useState(topics);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [managingCategories, setManagingCategories] = useState(false);
  // Slide direction for the category drill-down. Derived-state-during-render
  // (the React-sanctioned pattern) so the remounted wrapper carries the right
  // class on its very first frame: forward into a category, back to the top.
  const [prevCategoryId, setPrevCategoryId] = useState(selectedCategoryId);
  const [slideDir, setSlideDir] = useState("");
  if (prevCategoryId !== selectedCategoryId) {
    setPrevCategoryId(selectedCategoryId);
    setSlideDir(selectedCategoryId != null ? "forward" : "back");
  }
  const dragIndex = useRef(null);
  const dragOverIndex = useRef(null);

  const categoryIds = React.useMemo(() => new Set(categories.map(c => c.id)), [categories]);
  const currentCategory = selectedCategoryId != null ? categories.find(c => c.id === selectedCategoryId) || null : null;
  // A topic whose category vanished (or points at an unknown id) is treated as top-level.
  const inView = React.useCallback(
    (t) => (selectedCategoryId != null ? t.category === selectedCategoryId : !(t.category != null && categoryIds.has(t.category))),
    [selectedCategoryId, categoryIds],
  );
  const viewTopics = React.useMemo(() => topics.filter(inView), [topics, inView]);

  // Keep orderedTopics in sync when the parent refreshes topics (e.g. after import)
  // or the viewer moves between categories.
  React.useEffect(() => {
    if (!editMode) setOrderedTopics(viewTopics);
  }, [viewTopics, editMode]);

  // Leaving a category (or it being deleted under us) ends any edit session.
  React.useEffect(() => {
    setEditMode(false);
    setConfirmDeleteId(null);
    setSaveError(null);
  }, [selectedCategoryId]);

  // Per-category rollup for the cards: visible topic count, completed count,
  // and mean journey progress. Learners never see an empty category (nothing
  // to open); admins do, so they can assign topics into it.
  const categoryCards = React.useMemo(() => {
    if (selectedCategoryId != null) return [];
    return categories
      .map(cat => {
        const members = topics.filter(t => t.category === cat.id);
        let sum = 0, done = 0, tracked = 0;
        for (const t of members) {
          const p = topicProgress[t.id];
          if (p) { tracked += 1; sum += p.progress_percent ?? 0; if (p.completed) done += 1; }
        }
        const avg = tracked ? Math.round(sum / tracked) : 0;
        return { cat, count: members.length, done, avg, containsSelected: members.some(t => t.id === selectedTopicId) };
      })
      .filter(c => isAdmin || c.count > 0);
  }, [categories, topics, topicProgress, selectedCategoryId, selectedTopicId, isAdmin]);

  const topicCounts = React.useMemo(() => {
    const counts = {};
    for (const t of topics) if (t.category != null) counts[t.category] = (counts[t.category] || 0) + 1;
    return counts;
  }, [topics]);

  // Splice the (possibly reordered) view back into the global sequence: the
  // slots the view's topics occupied are refilled in the new order.
  function mergeIntoGlobal(nextView) {
    const viewIds = new Set(nextView.map(t => t.id));
    const full = [...topics];
    const slots = [];
    full.forEach((t, i) => { if (viewIds.has(t.id)) slots.push(i); });
    slots.forEach((slot, k) => { full[slot] = nextView[k]; });
    return full;
  }

  const hasAnyCategories = categories.length > 0;
  const nothingToShow = !topics.length && !(isAdmin && hasAnyCategories);

  if (nothingToShow && selectedCategoryId == null && !managingCategories) {
    return (
      <div>
        {isAdmin && (
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => setManagingCategories(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors hover:brightness-110"
              style={{ color: "var(--gold-bright)", borderColor: "var(--panel-line)", background: "transparent" }}
            >
              Categories
            </button>
          </div>
        )}
        <p className="text-sm px-1 py-4 text-center" style={{ color: "var(--parchment-dim)" }}>No topics yet.</p>
      </div>
    );
  }

  const displayTopics = editMode ? orderedTopics : viewTopics;

  // ── Drag handlers ──────────────────────────────────────────────────────────
  function handleDragStart(idx) {
    dragIndex.current = idx;
  }

  function handleDragEnter(idx) {
    dragOverIndex.current = idx;
    if (dragIndex.current === null || dragIndex.current === idx) return;
    setOrderedTopics(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex.current, 1);
      next.splice(idx, 0, moved);
      dragIndex.current = idx;
      return next;
    });
  }

  function handleDragEnd() {
    dragIndex.current = null;
    dragOverIndex.current = null;
  }

  // ── Delete topic ───────────────────────────────────────────────────────────
  async function handleDelete(topicId) {
    setDeleting(true);
    setSaveError(null);
    try {
      await deleteTopic(topicId);
      const next = orderedTopics.filter(t => t.id !== topicId);
      setOrderedTopics(next);
      setConfirmDeleteId(null);
      if (onReordered) onReordered(topics.filter(t => t.id !== topicId));
    } catch (e) {
      setSaveError("Failed to delete topic.");
    } finally {
      setDeleting(false);
    }
  }

  // ── Publish / unpublish (admin-owned topics only) ───────────────────────────
  async function handleToggleVisibility(topicId, nextPublic) {
    setTogglingId(topicId);
    setSaveError(null);
    try {
      await setTopicVisibility(topicId, nextPublic);
      const patch = t => (t.id === topicId ? { ...t, is_public: nextPublic } : t);
      setOrderedTopics(orderedTopics.map(patch));
      if (onReordered) onReordered(topics.map(patch));
    } catch (e) {
      setSaveError("Failed to update visibility.");
    } finally {
      setTogglingId(null);
    }
  }

  // ── Share / unshare with the institute (teachers + institute admins) ────────
  async function handleToggleInstituteShare(topic) {
    setTogglingId(topic.id);
    setSaveError(null);
    try {
      const { is_institute_shared } = await instituteShareTopic(topic.id, !topic.is_institute_shared);
      const patch = t => (t.id === topic.id ? { ...t, is_institute_shared } : t);
      setOrderedTopics(orderedTopics.map(patch));
      if (onReordered) onReordered(topics.map(patch));
    } catch (e) {
      setSaveError(e.message || "Failed to update sharing.");
    } finally {
      setTogglingId(null);
    }
  }

  // ── Move a topic between categories (admin, from Edit Order) ───────────────
  // The row stays in the edit list until Save/Cancel so drag slots line up;
  // the parent list is patched immediately so the card moves once editing ends.
  async function handleChangeCategory(topicId, rawValue) {
    const categoryId = rawValue === "" ? null : Number(rawValue);
    setTogglingId(topicId);
    setSaveError(null);
    try {
      await setTopicCategory(topicId, categoryId);
      const patch = t => (t.id === topicId ? { ...t, category: categoryId } : t);
      setOrderedTopics(orderedTopics.map(patch));
      if (onReordered) onReordered(topics.map(patch));
    } catch (e) {
      setSaveError(e.message || "Failed to move the topic.");
    } finally {
      setTogglingId(null);
    }
  }

  // ── Save order ─────────────────────────────────────────────────────────────
  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const full = mergeIntoGlobal(orderedTopics);
      await reorderTopics(full.map(t => t.id));
      setEditMode(false);
      if (onReordered) onReordered(full);
    } catch (e) {
      setSaveError("Failed to save order. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setOrderedTopics(viewTopics);
    setEditMode(false);
    setSaveError(null);
    setConfirmDeleteId(null);
  }

  const badgeClass = "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border leading-none";

  const editOrderLabel = currentCategory ? `Edit Order in "${currentCategory.name}"` : "Edit Order";

  return (
    // overflow-x clip keeps the slide's translateX from spawning a horizontal
    // scrollbar in the sidebar; the 4px side gutter leaves room for the card
    // hover nudge (2px) so it isn't clipped.
    <div className="overflow-x-clip px-1 -mx-1">
    <div
      key={selectedCategoryId ?? "root"}
      className={slideDir ? `topic-list-slide-${slideDir}` : ""}
      data-testid="topic-list-view"
      data-slide={slideDir || undefined}
    >
      {/* ── Inside a category: back link + heading ── */}
      {currentCategory && (
        <div className="mb-2.5" data-testid="category-header">
          <button
            type="button"
            onClick={() => onSelectCategory && onSelectCategory(null)}
            className="flex items-center gap-1 text-xs font-medium transition-colors hover:brightness-125 min-h-[32px]"
            style={{ color: "var(--gold-bright)" }}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            All categories
          </button>
          <h3 className="font-cormorant text-xl font-semibold leading-tight mt-1 truncate flex items-center gap-2" style={{ color: "var(--parchment)" }}>
            <CategoryIcon icon={currentCategory.icon} className="w-4 h-4 shrink-0" />
            <span className="truncate">{currentCategory.name}</span>
          </h3>
          {currentCategory.description && (
            <p className="text-xs mt-0.5" style={{ color: "var(--parchment-dim)" }}>{currentCategory.description}</p>
          )}
        </div>
      )}

      {/* ── Admin category manager (top level only) ── */}
      {isAdmin && managingCategories && !currentCategory && (
        <TopicCategoryManager
          categories={categories}
          topicCounts={topicCounts}
          onChanged={(next) => onCategoriesChanged && onCategoriesChanged(next)}
          onClose={() => setManagingCategories(false)}
        />
      )}

      {/* ── Admin edit-order toolbar ── */}
      {isAdmin && (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {!editMode ? (
            <>
              {viewTopics.length > 0 && (
                <button
                  onClick={() => { setOrderedTopics(viewTopics); setEditMode(true); setSaveError(null); setManagingCategories(false); }}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors hover:brightness-110"
                  style={{ color: "var(--gold-bright)", borderColor: "var(--panel-line)", background: "transparent" }}
                  title={currentCategory ? "Drag to reorder topics in this category" : "Drag to reorder topics"}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  Edit Order
                </button>
              )}
              {!currentCategory && !managingCategories && (
                <button
                  type="button"
                  onClick={() => setManagingCategories(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors hover:brightness-110"
                  style={{ color: "var(--gold-bright)", borderColor: "var(--panel-line)", background: "transparent" }}
                  title="Create, rename, reorder or delete topic categories"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                  </svg>
                  Categories
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold disabled:opacity-50 transition-colors hover:brightness-105"
                style={{ color: "var(--ink)", background: "var(--gold)" }}
              >
                {saving ? "Saving…" : "Save Order"}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors hover:brightness-110"
                style={{ color: "var(--parchment-dim)", borderColor: "var(--panel-line)", background: "transparent" }}
              >
                Cancel
              </button>
              <span className="text-xs ml-1" style={{ color: "var(--parchment-dim)" }} title={editOrderLabel}>Drag to reorder</span>
            </>
          )}
          {saveError && <span className="text-xs" style={{ color: "var(--tag-rose)" }}>{saveError}</span>}
        </div>
      )}

      {/* ── Category cards (top level) ── */}
      {categoryCards.length > 0 && !editMode && (
        <ul className="list-none p-0 space-y-2.5 mb-2.5" data-testid="category-cards">
          {categoryCards.map(({ cat, count, done, avg, containsSelected }) => (
            <li key={`cat-${cat.id}`}>
              <Surface
                as="button"
                glass
                interactive
                nudge
                active={containsSelected}
                onClick={() => onSelectCategory && onSelectCategory(cat.id)}
                className="w-full text-left px-4 py-3"
                style={count === 0 ? { opacity: 0.6 } : undefined}
                aria-label={`Open category ${cat.name}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 min-w-0">
                    <CategoryIcon icon={cat.icon} className="w-4 h-4 shrink-0" />
                    <span
                      className="font-cormorant text-lg font-semibold truncate"
                      style={{ color: containsSelected ? "var(--gold-bright)" : "var(--parchment)" }}
                    >
                      {cat.name}
                    </span>
                    {isAdmin && cat.is_restricted && (
                      <span
                        className="shrink-0"
                        style={{ color: "var(--gold-bright)" }}
                        title="Restricted: only category-group members (and admins) can see this"
                        data-testid="category-card-lock"
                      >
                        <LockIcon className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0 text-xs" style={{ color: "var(--parchment-dim)" }}>
                    <span className="tabular-nums">{count} {count === 1 ? "topic" : "topics"}</span>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
                {cat.description && (
                  <p className="text-xs mt-0.5 truncate" style={{ color: "var(--parchment-dim)" }}>{cat.description}</p>
                )}
                {count > 0 && (
                  <div className="mt-2.5 flex items-center gap-2">
                    <div className="progress-track flex-1">
                      <div className="progress-fill" style={{ width: `${avg}%` }} />
                    </div>
                    <span className="text-[0.72rem] tabular-nums shrink-0" style={{ color: "var(--parchment-dim)" }}>
                      {done}/{count} done
                    </span>
                  </div>
                )}
              </Surface>
            </li>
          ))}
        </ul>
      )}

      {currentCategory && displayTopics.length === 0 && (
        <p className="text-sm px-1 py-4 text-center" style={{ color: "var(--parchment-dim)" }}>
          {isAdmin ? "No topics in this category yet. Assign some from Edit Order or a topic's page." : "No topics in this category yet."}
        </p>
      )}

      <ul className="list-none p-0 space-y-2.5">
        {displayTopics.map((t, idx) => {
          const p = topicProgress[t.id];
          // Journey progress (steps completed), not quiz score — keeps the card
          // bar consistent with the guided journey's source of truth.
          const score = p ? (p.progress_percent ?? 0) : 0;
          const completed = p ? p.completed : false;
          const isSelected = t.id === selectedTopicId;
          const hasGrammar = !!t.grammar_rule_code;
          const hasVideo = !!t.has_video;
          const hasBook = !!t.has_book;

          if (editMode) {
            const isConfirming = confirmDeleteId === t.id;
            return (
              <li
                key={t.id}
                draggable={!isConfirming}
                onDragStart={() => !isConfirming && handleDragStart(idx)}
                onDragEnter={() => !isConfirming && handleDragEnter(idx)}
                onDragOver={e => e.preventDefault()}
                onDragEnd={handleDragEnd}
                className={isConfirming ? "" : "cursor-grab active:cursor-grabbing"}
              >
                <Surface
                  glass
                  active={isConfirming}
                  className="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 select-none"
                  style={{ color: "var(--parchment)" }}
                >
                  {/* Drag handle */}
                  {!isConfirming && (
                    <svg className="w-4 h-4 shrink-0" style={{ color: "var(--parchment-dim)" }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm8-12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>
                    </svg>
                  )}

                  {isConfirming ? (
                    <>
                      <span className="text-sm flex-1 font-medium" style={{ color: "var(--tag-rose)" }}>Delete "{t.name}"?</span>
                      <button
                        onClick={() => handleDelete(t.id)}
                        disabled={deleting}
                        className="px-2 py-0.5 rounded text-xs font-semibold disabled:opacity-50 transition-colors"
                        style={{ color: "#fff", background: "var(--tag-rose)" }}
                      >
                        {deleting ? "Deleting…" : "Yes, delete"}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        disabled={deleting}
                        className="px-2 py-0.5 rounded text-xs font-medium border transition-colors"
                        style={{ color: "var(--parchment-dim)", borderColor: "var(--panel-line)" }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="truncate flex-1">{t.name}</span>
                      {/* Publish / unpublish (only the admin's own topics can be toggled) */}
                      {isAdmin && t.is_owner && (
                        <button
                          onClick={() => handleToggleVisibility(t.id, !t.is_public)}
                          disabled={togglingId === t.id}
                          className="px-1.5 py-0.5 rounded text-[10px] font-semibold border shrink-0 transition-colors disabled:opacity-50"
                          style={t.is_public
                            ? { color: "var(--tag-teal)", background: "var(--tag-teal-bg)", borderColor: "transparent" }
                            : { color: "var(--slate)", background: "transparent", borderColor: "var(--panel-line)" }}
                          title={t.is_public ? "Public — click to make private" : "Private — click to publish to all learners"}
                        >
                          {togglingId === t.id ? "…" : t.is_public ? "Public" : "Private"}
                        </button>
                      )}
                      {hasAnyCategories && (
                        <select
                          value={t.category != null && categoryIds.has(t.category) ? String(t.category) : ""}
                          onChange={(e) => handleChangeCategory(t.id, e.target.value)}
                          disabled={togglingId === t.id}
                          onClick={(e) => e.stopPropagation()}
                          className="dt-field text-[11px] px-1.5 py-0.5 max-w-[7.5rem] shrink-0 disabled:opacity-50"
                          aria-label={`Category for ${t.name}`}
                          title="Move this topic to a category"
                        >
                          <option value="">No category</option>
                          {categories.map(c => (
                            <option key={c.id} value={String(c.id)}>{c.name}</option>
                          ))}
                        </select>
                      )}
                      <span className="text-xs shrink-0" style={{ color: "var(--parchment-dim)" }}>#{idx + 1}</span>
                      <button
                        onClick={() => setConfirmDeleteId(t.id)}
                        className="p-1 rounded shrink-0 transition-colors hover:brightness-125"
                        style={{ color: "var(--parchment-dim)" }}
                        title={`Delete "${t.name}"`}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </>
                  )}
                </Surface>
              </li>
            );
          }

          return (
            <li key={t.id}>
              <Surface
                as="button"
                glass
                interactive
                nudge
                active={isSelected}
                onClick={() => onSelect(t.id)}
                className="w-full text-left px-4 py-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="font-cormorant text-lg font-semibold truncate"
                    style={{ color: isSelected ? "var(--gold-bright)" : "var(--parchment)" }}
                  >
                    {t.name}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Admin's own unpublished topic — visible only to them until published. */}
                    {isAdmin && t.is_owner && !t.is_public && (
                      <span title="Private — only you can see this. Publish it from Edit Order." className={badgeClass} style={BADGE.private}>
                        <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm3 8H9V6a3 3 0 0 1 6 0v3z"/>
                        </svg>
                        Private
                      </span>
                    )}
                    {/* Institute sharing — teachers/institute-admins toggle their own
                        topics; other members just see the badge on shared ones. */}
                    {(instituteRole === "teacher" || instituteRole === "admin") && t.is_owner ? (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); handleToggleInstituteShare(t); }}
                        title={t.is_institute_shared
                          ? "Shared with your institute — click to unshare"
                          : "Click to share with your institute's members"}
                        className={`${badgeClass} cursor-pointer`}
                        style={t.is_institute_shared ? BADGE.institute : BADGE.private}
                      >
                        {togglingId === t.id ? "…" : t.is_institute_shared ? "Institute" : "Share"}
                      </span>
                    ) : t.is_institute_shared ? (
                      <span title="Shared within your institute" className={badgeClass} style={BADGE.institute}>
                        Institute
                      </span>
                    ) : null}
                    {hasBook && (
                      <span title="Created from a PDF / book" className={badgeClass} style={BADGE.book}>
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M4 4a2 2 0 012-2h9l5 5v13a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" opacity=".2" />
                          <path d="M6 3h8l4 4v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1zm7 1.5V8h3.5L13 4.5zM7.5 11h7v1.3h-7V11zm0 3h7v1.3h-7V14z" />
                        </svg>
                      </span>
                    )}
                    {hasVideo && (
                      <span title="Has YouTube video" className={badgeClass} style={BADGE.video}>
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                      </span>
                    )}
                    {hasGrammar && (
                      <span
                        title={`Grammar: ${t.grammar_rule_name} (${t.grammar_rule_arabic})`}
                        className={`${badgeClass} ${t.is_grammar_lesson ? "" : "font-arabic"}`}
                        style={BADGE.grammar}
                      >
                        <svg className="w-2.5 h-2.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 14.93V15a1 1 0 0 0-2 0v1.93A8 8 0 0 1 4.07 11H6a1 1 0 0 0 0-2H4.07A8 8 0 0 1 11 4.07V6a1 1 0 0 0 2 0V4.07A8 8 0 0 1 19.93 11H18a1 1 0 0 0 0 2h1.93A8 8 0 0 1 13 16.93z"/>
                        </svg>
                        {t.is_grammar_lesson ? "Grammar" : "نح"}
                      </span>
                    )}
                    {completed && (
                      <span className="text-xs" style={{ color: "var(--tag-teal)" }}>✓</span>
                    )}
                    {/* Book topics are personal — let the owner delete them. */}
                    {hasBook && !editMode && (
                      <span
                        role="button"
                        tabIndex={0}
                        title="Delete this book topic"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Delete "${t.name}"? This can't be undone.`)) {
                            handleDelete(t.id);
                          }
                        }}
                        className="p-1 rounded cursor-pointer transition-colors hover:brightness-125"
                        style={{ color: "var(--parchment-dim)" }}
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </span>
                    )}
                  </div>
                </div>

                {/* Mini progress bar — gold gradient on a dark track */}
                {p !== undefined && (
                  <div className="mt-2.5 flex items-center gap-2">
                    <div className="progress-track flex-1">
                      <div className="progress-fill" style={{ width: `${score}%` }} />
                    </div>
                    <span className="text-[0.72rem] tabular-nums shrink-0" style={{ color: "var(--parchment-dim)" }}>
                      {score}%
                    </span>
                  </div>
                )}
              </Surface>
            </li>
          );
        })}
      </ul>
    </div>
    </div>
  );
}
